import dayjs from "@/lib/helpers/dayjs";
import { NoteDetail } from "@/core/types";
import { testLocator } from "@/lib/helpers/test-locator";
import { useMatchSupervisor } from "@/features/cosignature/hooks/useMatchSupervisor";

import { nameWithCredentials } from "../helpers/name-with-credentials";
import { NoteSignature } from "../NoteSignature";
import { supervisorSignature } from "../helpers/supervisor-signature";

type SignatureProps = {
  className?: string;
  note: NoteDetail;
};

export function SignatureList({ className, note }: SignatureProps) {
  const {
    completed_date_time,
    cosigned_by,
    note_state,
    provider_cp_credentials,
    provider_practice,
    signature,
    signature_date,
  } = note;

  // Most note types set completed_date_time on submit (ISO string) → formatted here.
  // CM and ACA instead persist signature_date as a pre-formatted MM/DD/YYYY string;
  // an empty string means the note is a draft — show "None" to match prior CM behaviour.
  const signatureDate = completed_date_time
    ? dayjs(completed_date_time).format("MM/DD/YYYY")
    : signature_date === ""
      ? "None"
      : signature_date;

  const isComplete = note_state === "complete" || note_state === "completed";

  const { isLoading, supervisor } = useMatchSupervisor(cosigned_by);

  return (
    <footer className={className} {...testLocator("note-footer")}>
      <ul className="tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-4">
        {signature && (
          <NoteSignature isProvider label="Signature" {...testLocator("providerSignature")}>
            {nameWithCredentials({
              credentials: provider_cp_credentials,
              name: signature,
            })}
          </NoteSignature>
        )}

        {cosigned_by && isComplete && (isLoading || supervisor) && (
          <NoteSignature
            isLoading={isLoading}
            isProvider
            label="Co-signature"
            {...testLocator("coSignature")}
          >
            {supervisor &&
              supervisorSignature({
                credentials: supervisor.credentials,
                name: supervisor.name,
              })}
          </NoteSignature>
        )}

        {signatureDate && <NoteSignature label="Signature Date">{signatureDate}</NoteSignature>}

        {provider_practice && (
          <NoteSignature label="Provider Practice">{provider_practice}</NoteSignature>
        )}
      </ul>
    </footer>
  );
}
